import { TAB_INDEX } from './pasos';

/**
 * Paso del informe donde el cliente corrige cada error del catálogo de la SRT
 * (`Back-APICancerigenos/src/features/cancerigenos/domain/catalogo-errores-srt.mts`).
 *
 * `null` = no se corrige dentro del informe: datos del establecimiento o de su
 * contacto (se cargan en Establecimientos), la nómina (viene del RAR), el estado
 * de la presentación o un error del sistema. Esos van al resumen general, sin
 * contador de paso.
 *
 * Es una tabla explícita y no una regla por prefijo porque el anexo no alcanza:
 * el Anexo V mezcla sustancias y equipos radiológicos, y el I mezcla la cantidad
 * de trabajadores, los responsables y datos que ni siquiera están en el informe.
 * `pasoDeCorreccion.test.ts` recorre el catálogo entero para que un código nuevo
 * no quede sin decidir.
 */
export const PASO_POR_CODIGO: Readonly<Record<string, TAB_INDEX | null>> = {
    // Paso 1 · Datos generales
    I_TOTAL_TRABAJADORES_CERO: TAB_INDEX.ESTABLISHMENT_DATA,

    // Paso 2 · Sustancias (incluye medidas preventivas, EPP y capacitación por puesto)
    V_SUSTANCIA_INEXISTENTE: TAB_INDEX.SUBSTANCES,
    V_MEDIDA_PREVENTIVA_INEXISTENTE: TAB_INDEX.SUBSTANCES,
    V_EPP_INEXISTENTE: TAB_INDEX.SUBSTANCES,
    V_SIN_SUSTANCIAS: TAB_INDEX.SUBSTANCES,
    V_CAMPO_SUSTANCIA_FALTANTE: TAB_INDEX.SUBSTANCES,
    V_INFORMA_RIESGOS_SIN_DATO: TAB_INDEX.SUBSTANCES,
    V_CAPACITACION_RIESGOS_SIN_DATO: TAB_INDEX.SUBSTANCES,
    V_SUSTANCIA_DUPLICADA_EN_ESTABLECIMIENTO: TAB_INDEX.SUBSTANCES,
    V_ESTABLECIMIENTO_SIN_SUSTANCIAS: TAB_INDEX.SUBSTANCES,

    // Paso 4 · Equipos radiológicos (equipo, responsable de uso y encargado de protección)
    V_EQUIPO_RADIOLOGICO_INCOMPLETO: TAB_INDEX.RADIOLOGICAL_EQUIPMENT,
    V_RESPONSABLE_EQUIPO_INCOMPLETO: TAB_INDEX.RADIOLOGICAL_EQUIPMENT,
    V_EQUIPO_SIN_RESPONSABLE_USO: TAB_INDEX.RADIOLOGICAL_EQUIPMENT,
    V_EQUIPO_SIN_ENCARGADO_PROTECCION: TAB_INDEX.RADIOLOGICAL_EQUIPMENT,
    V_DOMICILIO_HABILITACION_SIN_DATO: TAB_INDEX.RADIOLOGICAL_EQUIPMENT,
    V_CUIL_RESPONSABLE_EQUIPO_INVALIDO: TAB_INDEX.RADIOLOGICAL_EQUIPMENT,
    V_EQUIPO_ID_CATALOGO_FALTANTE: TAB_INDEX.RADIOLOGICAL_EQUIPMENT,
    V_FECHA_EXPEDICION_FUTURA: TAB_INDEX.RADIOLOGICAL_EQUIPMENT,

    // Paso 5 · Trabajadores (actividades y fechas de exposición)
    III_EXPOSICION_FUERA_PERIODO: TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS,
    // Cruza con la fecha de ingreso de la nómina, pero se corrige con la fecha de
    // exposición, que se carga en este paso.
    III_EXPOSICION_ANTERIOR_INGRESO: TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS,
    III_SIN_TRABAJADORES: TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS,
    III_ACTIVIDAD_SIN_PUESTO_O_SECTOR: TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS,
    III_TRABAJADOR_SIN_ACTIVIDADES: TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS,
    III_INGRESO_POSTERIOR_PERIODO: TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS,
    III_FECHA_EXPOSICION_FALTANTE: TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS,
    III_ESTABLECIMIENTO_SIN_TRABAJADORES: TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS,
    III_ACTIVIDAD_SUPERPUESTA: TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS,

    // Paso 6 · Responsables
    I_CUIL_RESPONSABLE_INVALIDO: TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS,
    I_RESPONSABLE_DATOS_FALTANTE: TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS,
    I_RESPONSABLE_DUPLICADO: TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS,
    I_ROL_RESPONSABLE_FALTANTE: TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS,
    I_RESPONSABLE_SIN_DATOS_PROFESIONALES: TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS,
    I_RESPONSABLE_SIN_ROL_SRT: TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS,

    // Fuera del informe: resumen general
    I_CIIU_NO_REV4: null,
    I_CIIU_LONGITUD: null,
    // Aplica a la empresa, al contacto, a los responsables y a los contratistas.
    I_CUIT_NO_NUMERICO: null,
    // El CUIL del contacto no es un dato del establecimiento: el back lo toma
    // del responsable con cargo Responsable de Datos (o del primero con CUIL
    // válido). Se corrige en el paso Responsables.
    I_CUIL_CONTACTO_INVALIDO: TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS,
    I_CUIL_CONTACTO_FALTANTE: TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS,
    I_CAMPO_ESTABLECIMIENTO_FALTANTE: null,
    I_SIN_ESTABLECIMIENTOS: null,
    I_ESTABLECIMIENTO_NO_REGISTRADO_SRT: null,
    I_INFORME_ESTADO_NO_PERMITIDO: null,
    // La fecha de ingreso es de la nómina, no del informe.
    III_FECHA_INGRESO_FALTANTE: null,
    P_ESTADO_INVALIDO: null,
    P_PREVIA_SIN_FINALIZAR: null,
    GEN_PAYLOAD_NO_ARMABLE: null
};

/**
 * Paso para un código que todavía no está en la tabla (el back agregó uno y el
 * Front no se actualizó): una regla gruesa por anexo, mejor que perderlo.
 */
const pasoPorAnexo = (codigo: string, anexo?: string): TAB_INDEX | null => {
    if (anexo === 'III') return TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS;
    if (anexo === 'V')
        return codigo.includes('EQUIPO') ? TAB_INDEX.RADIOLOGICAL_EQUIPMENT : TAB_INDEX.SUBSTANCES;
    return null;
};

export const pasoDeCorreccion = (codigo: string, anexo?: string): TAB_INDEX | null =>
    codigo in PASO_POR_CODIGO ? PASO_POR_CODIGO[codigo] : pasoPorAnexo(codigo, anexo);

export interface CorreccionAgrupada {
    codigo: string;
    mensajeUsuario: string;
    paso: TAB_INDEX | null;
    /** Cuántas veces vino el código: una por registro afectado. */
    cantidad: number;
    /**
     * Qué dato falta y dónde, sin repetir: "Mail", "CIIU del sector (Planta
     * Norte, Depósito)". Es lo que vuelve concreto un mensaje genérico como
     * "Falta completar un dato obligatorio del establecimiento".
     */
    detalles: string[];
    /** Cómo resolverlo, del catálogo. */
    comoResolver?: string;
}

/** Texto de un dato faltante con los registros afectados, si los hay. */
const detalleDe = (campo?: string, registros?: Array<{ etiqueta: string }>): string | null => {
    if (!campo) return null;
    const etiquetas = (registros ?? []).map((r) => r.etiqueta).filter(Boolean);
    return etiquetas.length ? `${campo} (${etiquetas.join(', ')})` : campo;
};

/**
 * Agrupa las correcciones por código, en el orden de los pasos y con las
 * generales al final. El back manda una entrada por registro afectado, así que
 * dos sustancias sin cantidad llegan como dos errores con el mismo mensaje.
 */
export const agruparCorrecciones = (
    correcciones: Array<{
        codigo: string;
        anexo?: string;
        mensajeUsuario: string;
        comoResolver?: string;
        campo?: string;
        registros?: Array<{ etiqueta: string }>;
    }>
): CorreccionAgrupada[] => {
    const porCodigo = new Map<string, CorreccionAgrupada>();
    for (const { codigo, anexo, mensajeUsuario, comoResolver, campo, registros } of correcciones) {
        const detalle = detalleDe(campo, registros);
        const previa = porCodigo.get(codigo);
        if (previa) {
            previa.cantidad += 1;
            if (detalle && !previa.detalles.includes(detalle)) previa.detalles.push(detalle);
        } else
            porCodigo.set(codigo, {
                codigo,
                mensajeUsuario,
                paso: pasoDeCorreccion(codigo, anexo),
                cantidad: 1,
                detalles: detalle ? [detalle] : [],
                comoResolver
            });
    }
    const orden = (paso: TAB_INDEX | null) => (paso === null ? Number.MAX_SAFE_INTEGER : paso);
    return Array.from(porCodigo.values()).sort((a, b) => orden(a.paso) - orden(b.paso));
};

/** Cantidad de correcciones (sin agrupar) que caen en cada paso. */
export const contarCorreccionesPorPaso = (
    correcciones: Array<{ codigo: string; anexo?: string }>
): Partial<Record<TAB_INDEX, number>> => {
    const conteo: Partial<Record<TAB_INDEX, number>> = {};
    for (const { codigo, anexo } of correcciones) {
        const paso = pasoDeCorreccion(codigo, anexo);
        if (paso !== null) conteo[paso] = (conteo[paso] ?? 0) + 1;
    }
    return conteo;
};

/**
 * Dónde se corrige lo que no está en ningún paso del informe, para decírselo
 * al cliente. Los que no figuran (estado de la presentación, errores del
 * sistema, CUIT mal formados en varios lugares a la vez) no tienen un lugar
 * concreto adonde mandarlo.
 */
const LUGAR_FUERA_DEL_INFORME: Readonly<Record<string, string>> = {
    I_CIIU_NO_REV4: 'se corrige en Establecimientos',
    I_CIIU_LONGITUD: 'se corrige en Establecimientos',
    I_CAMPO_ESTABLECIMIENTO_FALTANTE: 'se corrige en Establecimientos',
    I_SIN_ESTABLECIMIENTOS: 'se corrige en Establecimientos',
    I_ESTABLECIMIENTO_NO_REGISTRADO_SRT: 'se corrige en Establecimientos',
    III_FECHA_INGRESO_FALTANTE: 'se corrige en la nómina'
};

/**
 * Correcciones que se arreglan editando los datos del establecimiento: la
 * etiqueta de lugar lleva a su página de detalle (ver `LugarCorreccion`).
 */
export const seCorrigeEnEstablecimiento = (codigo: string): boolean =>
    LUGAR_FUERA_DEL_INFORME[codigo] === 'se corrige en Establecimientos';

/**
 * Correcciones cuyo mensaje solo no alcanza para saber qué cambiar: se muestra
 * además el dato faltante y cómo resolverlo. Son las del establecimiento, la
 * del CUIL del contacto, que se toma de los responsables, y la del rol
 * obligatorio faltante, que viene una por cargo ("Falta: Profesional de…").
 */
export const necesitaDetalle = (codigo: string): boolean =>
    seCorrigeEnEstablecimiento(codigo) ||
    codigo === 'I_CUIL_CONTACTO_FALTANTE' ||
    codigo === 'I_CUIL_CONTACTO_INVALIDO' ||
    codigo === 'I_ROL_RESPONSABLE_FALTANTE';

/**
 * "paso Responsables", "se corrige en Establecimientos", o null si no hay un
 * lugar concreto. `nombrePaso` traduce el paso (lo resuelve quien llama, que
 * tiene el `t`).
 */
export const lugarDeCorreccion = (
    correccion: Pick<CorreccionAgrupada, 'codigo' | 'paso'>,
    nombrePaso: (paso: TAB_INDEX) => string
): string | null =>
    correccion.paso !== null
        ? `paso ${nombrePaso(correccion.paso)}`
        : (LUGAR_FUERA_DEL_INFORME[correccion.codigo] ?? null);
